import { Tooltip, Typography, alpha } from "@mui/material";
import React, { ReactElement, ReactNode } from "react";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";

export interface IMinimizedTooltipProps {
  children: ReactElement;
  title: React.ReactNode;
  delayNextEnter?: boolean;
  opacity?: number;
  placement?:
    | "bottom"
    | "bottom-end"
    | "bottom-start"
    | "left-end"
    | "left-start"
    | "left"
    | "right-end"
    | "right-start"
    | "right"
    | "top-end"
    | "top-start"
    | "top";
  disableHoverListener?: boolean;
  disableFocusListener?: boolean;
  disableTouchListener?: boolean;
  isExerciseLibrary?: boolean;
  isExerciseActionButton?: boolean;
  childrenClassName?: string;
  interactive?: boolean;
}

const useStyles = makeStyles({
  exerciseLibraryPopper: {
    zIndex: "99999999 !important",
  },
  exerciseActionButton: {
    width: "100%",
  },
});

export const TooltipLine = ({ children }: { children: ReactNode }) => (
  <Typography variant="hint">{children}</Typography>
);

/**
 * @note If you need a tooltip that opens on click and stays open until user clicks away or on the element again
 *  use ClickableMinimizedTooltip instead
 */

const MinimizedTooltip = ({
  children,
  placement,
  title,
  opacity = 0.5,
  delayNextEnter = true,
  disableHoverListener,
  disableFocusListener = true,
  disableTouchListener,
  isExerciseLibrary = false,
  isExerciseActionButton = false,
  childrenClassName,
  interactive = false,
}: IMinimizedTooltipProps) => {
  const s = useStyles();
  return (
    <Tooltip
      arrow
      placement={placement}
      enterDelay={300}
      enterNextDelay={delayNextEnter ? 3000 : 300}
      disableInteractive={!interactive}
      title={title}
      disableHoverListener={disableHoverListener}
      disableFocusListener={disableFocusListener}
      disableTouchListener={disableTouchListener}
      classes={{
        popper: isExerciseLibrary && s.exerciseLibraryPopper,
      }}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? alpha(theme.palette.grey[800], opacity)
                : alpha(theme.palette.text.primary, opacity),
            color: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.common.white
                : undefined,
            px: 1,
            py: 0.8,
            userSelect: "none",
          },
        },
        arrow: {
          sx: {
            color: (theme) =>
              theme.palette.mode === "dark"
                ? alpha(theme.palette.grey[800], opacity)
                : alpha(theme.palette.text.primary, opacity),
          },
        },
      }}
    >
      <span
        className={clsx(
          isExerciseActionButton && s.exerciseActionButton,
          childrenClassName,
        )}
      >
        {children}
      </span>
    </Tooltip>
  );
};

export default MinimizedTooltip;
